import { logger } from '@origin-digital/reporting-client';
import * as auth0 from 'auth0-js';
import { getWindowLocationWithoutHashOrQuery } from 'ts/window/WindowUtils';
import * as ConfigService from '../../config/ConfigService';
import { removeProtocol } from '../../login/services/urlUtil';
import * as Window from '../../window/Window';
import * as Auth0LocalStorage from '../authlocalstorage/auth0LocalStorage';
import { mockAuth0WebAuth } from './MockAuth0';
import Axios from 'axios';

const AUDIENCE = 'audience';
export interface IdTokenPayload {
  email: string;
  'https://originenergy.com.au/authorization': {
    permissions: string[];
  };
}

export interface IAuthResult {
  accessToken?: string;
  idToken?: string;
  expiresIn?: string;
  idTokenPayload: IdTokenPayload;
}

const createWebAuth = (callbackUrl: string) => {
  const isStubAuth0 = process.env.STUB_AUTH0 || false;
  if (isStubAuth0) {
    return mockAuth0WebAuth();
  }
  return new auth0.WebAuth({
    domain: ConfigService.getAuth0Domain(),
    clientID: ConfigService.getAuth0ClientId(),
    redirectUri: callbackUrl,
    responseType: 'token id_token',
    scope: 'openid email read:api',
    audience: 'https://digitalapi',
    overrides: {
      __tenant: ConfigService.getAuth0Tenant(),
      __token_issuer: ConfigService.getAuth0Host() + '/',
    },
  });
};

const createWebAuthHosted = (callbackUrl: string) => {
  const isStubAuth0 = process.env.STUB_AUTH0 || false;
  if (isStubAuth0) {
    return mockAuth0WebAuth();
  }
  const urlParams = new URLSearchParams(Window.getWindow().location.search);
  const auth0ConfigObj = Window.getWindow().auth0Config;
  const scope = auth0ConfigObj.extraParams.scope
    ? auth0ConfigObj.extraParams.scope
    : 'openid email read:api';
  let responseType = auth0ConfigObj.extraParams.response_type;
  if (!responseType) {
    responseType = auth0ConfigObj.extraParams.type;
  }
  const audience = urlParams.get(AUDIENCE) ? urlParams.get(AUDIENCE) : 'https://digitalapi';
  const redirectUri = auth0ConfigObj.callbackURL ? auth0ConfigObj.callbackURL : callbackUrl;
  return new auth0.WebAuth({
    domain: removeProtocol(auth0ConfigObj.authorizationServer.url),
    clientID: ConfigService.getAuth0ClientId(),
    redirectUri,
    responseType,
    scope,
    audience,
    overrides: {
      __tenant: ConfigService.getAuth0Tenant(),
      __token_issuer: ConfigService.getAuth0Host() + '/',
    },
    ...auth0ConfigObj.extraParams,
    ...auth0ConfigObj.internalOptions,
  });
};

const getConnectionType = (phoneOrEmail: string) => {
  return /@/.test(phoneOrEmail) ? 'email' : 'sms';
};

const getConnectionParams = (phoneOrEmail: string) => {
  const connection = getConnectionType(phoneOrEmail);
  return connection === 'email'
    ? { connection, email: phoneOrEmail }
    : { connection, phoneNumber: convertDomesticToInternationalPhone(phoneOrEmail) };
};

const convertDomesticToInternationalPhone = (phoneNumber: string): string => {
  return phoneNumber.replace(/^0/, '+61');
};

const triggerPasswordlessStart = (phoneOrEmailInput: string, type: string = 'code') => {
  const connectionParams = getConnectionParams(phoneOrEmailInput);
  const authObject = {
    send: type,
    ...connectionParams,
  };

  return promisedPasswordlessStart(authObject);
};

const getPasswordlessWebAuth = () => {
  const isStubAuth0 = process.env.STUB_AUTH0 || false;
  if (isStubAuth0) {
    return mockAuth0WebAuth();
  }
  if (!Window.getWindow().passwordlessWebAuth) {
    Window.getWindow().passwordlessWebAuth = createWebAuthHosted(
      getWindowLocationWithoutHashOrQuery(),
    );
  }
  return Window.getWindow().passwordlessWebAuth;
};

const promisedPasswordlessStart = (authObject): Promise<{}> => {
  const webAuth = getPasswordlessWebAuth();
  return new Promise((resolve, reject) => {
    webAuth.passwordlessStart(authObject, (error, authResult) => {
      if (error) {
        reject(error);
      }
      resolve(authResult);
    });
  });
};

const verifyOneTimeCode = (phoneOrEmail, code, flow, onResponse) => {
  const connectionParams = getConnectionParams(phoneOrEmail);
  const authObject = {
    ...connectionParams,
    verificationCode: code,
    flow,
  };
  const webAuth = getPasswordlessWebAuth();
  webAuth.passwordlessLogin(authObject, onResponse);
};

const storeAuth0Tokens = (authResult: IAuthResult, currentTime = null) => {
  if (!isValidAuthResult(authResult)) {
    logger.error('Failed to validate auth result');
    return;
  }

  currentTime = currentTime ? currentTime : Date.now();
  const expiresAt = currentTime + parseInt(authResult.expiresIn, 10) * 1000;

  Auth0LocalStorage.setAll(authResult.accessToken, authResult.idToken, expiresAt);
};

const isValidAuthResult = (authResult: Partial<IAuthResult>): boolean => {
  return notEmpty(authResult) && notEmpty(authResult.accessToken) && notEmpty(authResult.idToken);
};

const notEmpty = (it) => {
  return it !== undefined && it !== null;
};

const logoutAndRedirect = (redirectUrl: string) => {
  const encodedRedirectUrl = encodeURIComponent(redirectUrl);
  // note: the redirect url must be added to the allowed logout urls in auth0
  // This request will return a http 302 which will redirect, we
  // want to redirect to the full current url with the given pageRoute
  // Ideally, we handle the redirect ourselves but the logout url MUST do a redirect
  // I dont see a good way to get around the redirect without something hacky
  Window.getWindow().location.replace(
    `https://${ConfigService.getAuth0Domain()}/v2/logout?returnTo=${encodedRedirectUrl}`,
  );
};

export {
  triggerPasswordlessStart,
  verifyOneTimeCode,
  storeAuth0Tokens,
  createWebAuthHosted,
  createWebAuth,
  logoutAndRedirect,
};
